import { zodResolver } from "@hookform/resolvers/zod"
import { Box, Button } from "@mui/material"
import { useNavigate, useSearch } from "@tanstack/react-router"
import type { FC } from "react"
import React from "react"
import { FormProvider, useForm } from "react-hook-form"

import PriceChangeForm from "@/screens/Pricing/components/forms/PriceChangeFilterForm"
import type { PriceChangeFormValues } from "@/screens/Pricing/schema/PriceChangeSchema"
import { PriceChangeFormSchema } from "@/screens/Pricing/schema/PriceChangeSchema"
import { isFieldFilled } from "@/utils/forms"

const PriceChangeFilter: FC = () => {
  const navigate = useNavigate({ from: "/app/pricing/price-change" })
  const search = useSearch({
    from: "/app/pricing/_layout/price-change",
    strict: true,
  })

  const {
    changeType = "",
    supplierId = "",
    customerIds = [],
    effectiveDate = "",
    productCategoryId = "",
    productClassId = "",
    specificProductIds = [],
    shipFrom = "",
    shipTo = "",
  } = search

  const hasFilterParams = Boolean(Object.keys(search).length)

  const methods = useForm({
    defaultValues: {
      changeType,
      supplierId,
      customerIds,
      productCategoryId,
      productClassId,
      specificProductIds,
      shipFrom,
      shipTo,
      effectiveDate,
    },
    mode: "onChange",
    resolver: zodResolver(PriceChangeFormSchema),
  })

  const {
    formState: { isDirty, isValid },
    handleSubmit,
  } = methods

  const onSubmitData = async (data: PriceChangeFormValues) => {
    await navigate({
      search: {
        ...search,
        changeType: data.changeType,
        supplierId: isFieldFilled(data?.supplierId) ? data.supplierId : undefined,
        customerIds: isFieldFilled(data?.customerIds) ? data.customerIds : undefined,
        productCategoryId: isFieldFilled(data?.productCategoryId) ? data.productCategoryId : undefined,
        productClassId: isFieldFilled(data?.productClassId) ? data.productClassId : undefined,
        specificProductIds: isFieldFilled(data?.specificProductIds) ? data.specificProductIds : undefined,
        shipFrom: isFieldFilled(data?.shipFrom) ? data?.shipFrom : undefined,
        shipTo: isFieldFilled(data?.shipTo) ? data?.shipTo : undefined,
        effectiveDate: isFieldFilled(data?.effectiveDate) ? (data.effectiveDate as string) : undefined,
        page: undefined,
      },
      replace: true,
    })
  }

  const handleClear = async () => {
    await navigate({
      search: (prev) => ({
        ...prev,
        changeType: undefined,
        supplierId: undefined,
        customerIds: undefined,
        productCategoryId: undefined,
        productClassId: undefined,
        specificProductIds: undefined,
        shipFrom: undefined,
        shipTo: undefined,
        effectiveDate: undefined,
        page: undefined,
      }),
      replace: true,
    })
  }

  return (
    <Box>
      <FormProvider {...methods}>
        <PriceChangeForm />
      </FormProvider>
      <Box className='mt-1 flex flex-row items-end justify-end gap-3'>
        <Button
          className='capitalize'
          type='submit'
          variant='outlined'
          disabled={!isValid || !isDirty}
          onClick={handleSubmit(onSubmitData)}
        >
          Apply Filters
        </Button>
        <Button className='capitalize' disabled={!hasFilterParams} onClick={handleClear}>
          Clear all
        </Button>
      </Box>
    </Box>
  )
}

export default PriceChangeFilter
